import { useState } from 'react';
import { useLoaded } from '../lib/useLoaded.ts';
import { LoadingNotice, EmptyNotice, ErrorNotice } from '../components/StateNotice.tsx';
import type { Deliverable } from '../lib/contracts.ts';
import type { DataPort } from '../adapters/port.ts';

/**
 * Biblioteca — los entregables producidos por el motor, con su concepto y la
 * pieza abrible. Acá aterriza lo que genera Anthropic tras una aprobación.
 */
export function Library({ port }: { port: DataPort }) {
  const d = useLoaded(() => port.deliverables());
  return (
    <section>
      <p className="lede">Entregables producidos por el motor, listos para revisar. Cada uno con el concepto y la pieza abrible.</p>
      {d.kind === 'loading' && <LoadingNotice what="los entregables" />}
      {d.kind === 'error' && <ErrorNotice message={d.message} />}
      {d.kind === 'empty' && <EmptyNotice what="entregables" />}
      {d.kind === 'ok' && (
        <div className="rows">
          {d.value.map((x) => <DeliverableCard key={x.id} d={x} />)}
        </div>
      )}
    </section>
  );
}

function DeliverableCard({ d }: { d: Deliverable }) {
  const [abierto, setAbierto] = useState(false);

  // La pieza se lleva DESCARGÁNDOLA, no abriéndola.
  //
  // 🔴 Antes esto fabricaba una URL `blob:` con el HTML del modelo y la abría
  // con `window.open`. Una URL de objeto HEREDA EL ORIGEN de quien la creó: el
  // entregable se ejecutaba en creative.logisticatops.com con la cookie de
  // sesión, exactamente igual que el iframe sin sandbox. Y era peor de cerrar,
  // porque una ventana abierta con `window.open` no admite `sandbox`: no había
  // forma de aislarla. Sandboxear la vista previa y dejar esta vía abierta no
  // cerraba nada — era la misma vulnerabilidad por otra puerta.
  //
  // Se empaqueta como binario opaco y NO como `text/html`: si alguien navegara
  // a esta URL —hoy, o por una edición futura— el navegador la bajaría en vez
  // de renderizarla. El aislamiento deja de depender de que nadie navegue.
  //
  // El usuario no pierde la pieza renderizada: la vista previa embebida, ya
  // aislada, sigue mostrándola. Lo que cambia es que llevársela es ahora una
  // descarga explícita, y abrir ese archivo queda bajo su control, fuera de
  // Nexus y en otro origen.
  function descargarPieza() {
    if (!d.content) return;
    const blob = new Blob([d.content], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${d.name}.html`;
    a.rel = 'noopener';
    a.click();
    // Un respiro antes de revocar: hacerlo en el mismo tick cancela la descarga
    // en algunos navegadores.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }

  return (
    <article className="deliverable">
      <div className="deliverable__head">
        <strong>{d.name}</strong>
        <span className={`chip-state chip-state--${d.status === 'review' ? 'warn' : d.status === 'approved' || d.status === 'published' ? 'ok' : ''}`}>
          {d.status === 'review' ? 'Listo para revisar' : d.status}
        </span>
      </div>

      {d.summary && <p className="deliverable__concept">{d.summary}</p>}

      <div className="deliverable__meta mono">
        {d.format}{d.model ? ` · generado por ${d.model}` : ''} · v{d.version}
      </div>

      {/* Un video no se guarda en la base —pesa cientos de KB— así que vive en
          Drive y ése es su único modo de abrirse. Sin este enlace la tarjeta
          quedaba muerta: listada pero imposible de ver. */}
      {(d.content || d.driveLink) && (
        <div className="deliverable__actions">
          {/* «Descargar» y no «Abrir»: el rótulo dice lo que efectivamente pasa.
              Abrir el archivo bajado corre por cuenta del usuario, en otro
              origen — que es justamente lo que lo vuelve seguro. */}
          {d.content && (
            <button type="button" className="btn-primary" onClick={descargarPieza}>Descargar el HTML</button>
          )}
          {d.driveLink && (
            <a className={d.content ? 'btn-ghost' : 'btn-primary'} href={d.driveLink}
               target="_blank" rel="noopener">
              {d.format.startsWith('video/') ? 'Ver el video en Drive' : 'Abrir en Drive'}
            </a>
          )}
          {d.content && (
            <button type="button" className="btn-ghost" onClick={() => setAbierto((v) => !v)}>
              {abierto ? 'Ocultar vista previa' : 'Vista previa'}
            </button>
          )}
        </div>
      )}

      {/* La pieza la escribió un modelo a partir del pedido de un usuario, así que
          es HTML AJENO. `srcDoc` no crea un origen nuevo: sin `sandbox` el
          documento hereda el de la aplicación, y un <script> adentro del
          entregable corría en creative.logisticatops.com con la cookie de
          sesión —podía aprobar planes, disparar generaciones pagas y leer
          /api/*—. No hacía falta un ataque: el motor genera landings, y una
          landing legítimamente trae <script>.

          `sandbox` vacío = todas las restricciones. Para MOSTRAR contenido
          estático no hace falta ningún permiso: el CSS, las imágenes y las
          fuentes por URL absoluta siguen cargando. Deliberadamente más estricto
          que SocialStill.tsx, que concede `allow-same-origin`: ahí el padre es
          el renderizador de Remotion, acá es la sesión del usuario, y conceder
          el origen no aporta nada a una vista previa. Lo que sí se pierde es el
          JS de la pieza — es exactamente el punto.

          ⚠️ Nunca agregar `allow-scripts`, y menos junto a `allow-same-origin`:
          esa pareja anula el aislamiento por completo. AISLA-1 y AISLA-2 lo verifican. */}
      {abierto && d.content && (
        <iframe className="deliverable__frame" title={`Vista previa · ${d.name}`} srcDoc={d.content} sandbox="" />
      )}
    </article>
  );
}
